import { AnnotationMetadata } from '@/components/AnnotationMetadata';
import { ColorPickerMenu } from '@/components/ColorPickerMenu';
import { ClearIcon, IconButton } from '@/components/IconButton';
import { PopupMenu } from '@/components/PopupMenu';
import { useExplore } from '@/stores/explore';
import {
  Layer,
  loadLayerModel,
  Measurement,
  setTargetMeasurement,
  useViewer,
} from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { getLayerIcon } from '@/utils/nodeIcons';
import { truncateText } from '@/utils/truncateText';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Button,
  chip,
  chipIcon,
  Input,
  Select,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@skand/ui';
import {
  areaToString,
  calculateEdgeLengths,
  calculatePolygonArea,
  Cartographic,
  lengthToString,
  Model3D,
  ModelNode,
  SceneNode,
  VolumetricBaseMode,
  Volumetrics,
} from '@skand/viewer-component-v2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Color, Matrix4 } from 'three';
import {
  VolumetricBaseOption,
  VOLUMETRICS_BASE_OPTIONS,
} from '../AnnotationPopupMenu/AnnotationMeasurements/AnnotationMeasurements';
import { Item } from '../AnnotationPopupMenu/AnnotationMeasurements/Item';
import { LayerSelector } from '../AnnotationPopupMenu/AnnotationMeasurements/LayerSelector';
import { Loader } from '../AnnotationPopupMenu/AnnotationMeasurements/Loader';
import { Point } from '../AnnotationPopupMenu/AnnotationMeasurements/Point';
import { SectioningView } from '../AnnotationPopupMenu/AnnotationMeasurements/SectioningView';
import { Segments } from '../AnnotationPopupMenu/AnnotationMeasurements/Segments';
import { VolumetricsView } from '../AnnotationPopupMenu/AnnotationMeasurements/VolumetricsView';
import { RemoveCalculationModal } from '../AnnotationPopupMenu/RemoveCalculationModal';
interface MeasurementPopupMenuProps {
  targetMeasurement: Measurement;
}

export const MeasurementPopupMenu = ({ targetMeasurement }: MeasurementPopupMenuProps) => {
  const api3D = useViewer(state => state.api3D);

  const showLocationAlertModal = useExplore(state => state.showLocationAlertModal);
  const measurementUnit = useExplore(state => state.measurementUnit);
  const annotationDraft = useExplore(state => state.annotationDraft);

  const [enabledPoints, setEnabledPoints] = useState(false);
  const [enabledLayerSelector, setEnabledLayerSelector] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<Layer | null>(null);
  const [sections, setSections] = useState<Cartographic[][] | null>(null);
  const [volumetrics, setVolumetrics] = useState<Volumetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [baseSurface, setBaseSurface] = useState<VolumetricBaseOption>('smart');
  const [baseHeight, setBaseHeight] = useState<number>(0);
  const [enabledRemoveCalculationModal, setEnabledRemoveCalculationModal] = useState(false);
  const [annotationColor, setAnnotationColor] = useState(targetMeasurement.sketch.getColor());

  const enabledClearButton = volumetrics || sections;

  const { points, isClosed } = useMemo(() => {
    const isClosed = targetMeasurement.sketch.getType() === 'polygon';
    const worldTransform = new Matrix4().compose(
      targetMeasurement.sketch.getPosition(),
      targetMeasurement.sketch.getRotation(),
      targetMeasurement.sketch.getScale(),
    );

    const points = targetMeasurement.sketch
      .getVertices()
      .map(vertex => vertex.applyMatrix4(worldTransform));

    return {
      points,
      isClosed,
    };
  }, [targetMeasurement]);

  const getModelNode = useCallback(
    async (layer: Layer): Promise<Model3D | null> => {
      if (layer.sceneNode.constructor === SceneNode) {
        const error = await loadLayerModel(layer);
        if (error) {
          useExplore.setState({ placeLayerTarget: layer });
          showLocationAlertModal();
        }
      }
      if (layer.sceneNode instanceof ModelNode) {
        return layer.sceneNode.getModel() as Model3D;
      }
      return null;
    },
    [showLocationAlertModal],
  );

  // Compute the sectioning
  const computeSectioning = useCallback(
    async (layer: Layer) => {
      setIsLoading(true);
      const model = await getModelNode(layer);
      if (api3D && model) {
        api3D.sectioning.clear();
        const sections = await api3D.sectioning.compute(points, model);
        setSections(sections);
      }
      setIsLoading(false);
    },
    [api3D, getModelNode, points],
  );

  // Compute the volumetrics
  const computeVolumetrics = useCallback(
    async (layer: Layer, baseMode: VolumetricBaseMode) => {
      setIsLoading(true);
      const model = await getModelNode(layer);
      if (api3D && model) {
        api3D.volumetric.clear();
        const volumetrics = await api3D.volumetric.compute(points, baseMode, model);
        setVolumetrics(volumetrics);
      }
      setIsLoading(false);
    },
    [api3D, getModelNode, points],
  );

  const handleClose = useCallback(async () => {
    if (!targetMeasurement) return;
    await targetMeasurement.sketch.destroy();
    await targetMeasurement.sketch.hideMeasurements();
    useViewer.setState(state => ({
      measurements: state.measurements.filter(m => m !== targetMeasurement),
    }));

    setTargetMeasurement(null);
    api3D?.volumetric.hide();
    api3D?.sectioning.hide();
    setSections(null);
    setVolumetrics(null);
  }, [api3D?.sectioning, api3D?.volumetric, targetMeasurement]);

  const [selectedLayerName, selectedLayerIcon] = useMemo(() => {
    const layerName = selectedLayer?.name ?? 'Select Layer';
    const layerIcon = selectedLayer?.formatType ?? null;
    return [layerName, layerIcon];
  }, [selectedLayer]);

  const shapeType = useMemo(() => {
    if (points.length === 1) return 'Point';
    else if (isClosed) return 'Polygon';
    else return 'Polyline';
  }, [isClosed, points.length]);

  const measurementSubTitle = useMemo(() => {
    switch (shapeType) {
      case 'Polyline':
        return 'Generate elevation profile';
      case 'Polygon':
        return 'Calculate volumetric';
    }
  }, [shapeType]);

  const area = useMemo(() => calculatePolygonArea(points), [points]);

  const totalLength = useMemo(() => {
    const lengths = calculateEdgeLengths(points, isClosed);
    return lengths.reduce((prev, curr) => prev + curr, 0);
  }, [isClosed, points]);

  // Update sketch and mesh overlay visibility
  useEffect(() => {
    if (volumetrics && shapeType === 'Polygon') {
      targetMeasurement.sketch.hide();
      api3D?.volumetric.show();
      api3D?.volumetric.setColor(annotationColor);
    } else if (sections && shapeType === 'Polyline') {
      targetMeasurement.sketch.hide();
      api3D?.sectioning.show();
      api3D?.sectioning.setColor(annotationColor);
    } else {
      targetMeasurement.sketch.show();
    }
  }, [targetMeasurement, api3D, sections, volumetrics, annotationColor, shapeType]);

  // Hide measurement popup menu when annotation popup menu is opened
  useEffect(() => {
    if (annotationDraft) {
      handleClose();
    }
  }, [annotationDraft, handleClose]);

  const handleColor = (colorHex: Color) => {
    setAnnotationColor(colorHex);
    targetMeasurement.sketch.setColor(new Color(colorHex));
  };

  // Handle selecting a layer
  const handleSelectLayer = (layer: Layer) => {
    setVolumetrics(null);
    setSections(null);
    setSelectedLayer(layer);
  };

  const handleOpenRemoveCalculationModal = () => {
    setEnabledRemoveCalculationModal(true);
  };

  const handleCloseRemoveCalculationModal = () => {
    setEnabledRemoveCalculationModal(false);
  };

  const handleConfirmRemoveCalculation = () => {
    setSections(null);
    setVolumetrics(null);
    api3D?.sectioning.hide();
    api3D?.volumetric.hide();
    setEnabledRemoveCalculationModal(false);
  };

  // Handle selecting the base surface for volumetrics
  const handleSelectBaseSurface = (item: VolumetricBaseOption) => {
    setVolumetrics(null);
    setSections(null);
    setBaseSurface(item);
    useExplore.setState({ annotationBaseSurfaceCache: item });
  };

  // Handle changing the custom base height for volumetrics
  const handleChangeBaseHeight = (height: number) => {
    setVolumetrics(null);
    setSections(null);
    if (isNaN(height)) {
      setBaseHeight(0);
    } else {
      setBaseHeight(height);
    }
  };

  const ActionButton = () => {
    if (shapeType === 'Polyline') {
      return (
        <Button
          className="mt-3 w-full"
          disabled={!selectedLayer}
          onClick={() => selectedLayer && computeSectioning(selectedLayer)}
          primary
          size="small"
        >
          Generate
        </Button>
      );
    } else if (shapeType === 'Polygon') {
      return (
        <Button
          className="mt-3 w-full"
          disabled={!selectedLayer}
          onClick={() =>
            selectedLayer &&
            computeVolumetrics(
              selectedLayer,
              baseSurface === 'custom' ? baseHeight : (baseSurface as VolumetricBaseMode),
            )
          }
          primary
          size="small"
        >
          Calculate
        </Button>
      );
    }
  };

  return (
    <PopupMenu
      containerStyles={cn(
        'w-[387px]',
        'flex',
        'bg-neutral-100',
        'rounded-2',
        'border-1px',
        'border-solid',
        'border-neutral-400',
        'flex-col',
        'min-h-10vh',
        'p-3',
      )}
      disableCloseButton
      y={40}
    >
      <div
        className={cn(
          'flex',
          'justify-between',
          'items-end',
          'border-neutral-300',
          'border-1',
          'border-b-solid',
          'pb-3',
          'flex-none',
        )}
      >
        <div className="flex flex-row items-center justify-center">
          <p className={cn('typo-text-l color-neutral-800')}>Measurements</p>
          <button
            className={cn(
              'bg-contain',
              'h-5 w-5',
              'rounded-[4px]',
              'b-1px b-solid b-neutral-500',
              'ml-3',
              'hover:cursor-pointer',
            )}
            onClick={() => setShowColorPicker(!showColorPicker)}
            style={{ backgroundColor: `#${annotationColor.getHexString()}` }}
          />
          {showColorPicker && (
            <ColorPickerMenu
              closeMenu={() => setShowColorPicker(false)}
              color={annotationColor}
              selectColor={handleColor}
              x={100}
              y={200}
            />
          )}
        </div>
        <IconButton
          buttonIcon={<div className={cn('i-skand-close text-4', 'text-neutral-500')} />}
          buttonState="default"
          className={cn('bg-neutral-100', 'cursor-pointer', 'border-none')}
          onClick={handleClose}
        />
      </div>
      <div className="max-h-70vh overflow-auto">
        <p className="pointer-events-none mt-2 text-neutral-800 typo-text-m-em">{shapeType}</p>
        {shapeType === 'Point' ? (
          <div className="skand-rnd-disable px-1">
            <Point position={points[0]} />
          </div>
        ) : (
          <>
            <div className="skand-rnd-disable px-1">
              {shapeType === 'Polyline' && <Segments points={points} />}
              <Item
                label={shapeType === 'Polygon' ? 'Perimeter' : 'Total length'}
                value={lengthToString(totalLength, measurementUnit, 4)}
              />
              {shapeType === 'Polygon' && area && (
                <Item label="Area" value={areaToString(area, measurementUnit, 4)} />
              )}
            </div>

            <div className="mt-3 flex flex-row items-center justify-between border-t-1 border-t-neutral-300 border-t-solid pt-3">
              <p className="pointer-events-none text-neutral-800 typo-text-m-em">
                {measurementSubTitle}
              </p>
              {enabledClearButton && (
                <IconButton
                  buttonIcon={<ClearIcon />}
                  buttonState="default"
                  className={cn('bg-neutral-100 cursor-pointer')}
                  onClick={handleOpenRemoveCalculationModal}
                />
              )}
            </div>

            {shapeType === 'Polygon' && (
              <>
                <p className="pointer-events-none mb-2 mt-3 text-neutral-600 typo-text-s">
                  Base surface
                </p>
                <Select
                  onChange={handleSelectBaseSurface}
                  options={[...VOLUMETRICS_BASE_OPTIONS]}
                  style={{ display: 'block' }}
                  value={baseSurface}
                  width="full"
                />
                {baseSurface === 'custom' && (
                  <div className="mt-3">
                    <Input
                      onChange={e => handleChangeBaseHeight(parseFloat(e))}
                      placeholder="Specify height in meters"
                      value={baseHeight.toString()}
                    />
                  </div>
                )}
              </>
            )}
            <div className="mb-2 mt-3 flex flex-row items-center">
              <p className="pointer-events-none text-neutral-600 typo-text-s">
                Measurement reference
              </p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'i-skand-info',
                      'color-neutral-500',
                      'ml-2',
                      'text-3.5',
                      'cursor-pointer',
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <div className={chip({ intent: 'info' })}>
                    <div className={chipIcon({ intent: 'info' })} />
                    Supported layer types include 3D Mesh, IFC, and OBJs (no terrain).
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <DropdownMenu.Root
              modal={false}
              onOpenChange={setEnabledLayerSelector}
              open={enabledLayerSelector}
            >
              <DropdownMenu.Trigger asChild>
                <div
                  className={cn(
                    'mt-2',
                    'flex',
                    'flex-row',
                    'items-center',
                    'justify-between',
                    'rounded-1',
                    'border-neutral-400',
                    'border-1',
                    'border-solid',
                    'h-9',
                    'px-3',
                    'cursor-pointer',
                  )}
                >
                  <div className="flex flex-1 items-center gap-1">
                    {selectedLayerIcon && (
                      <div
                        className={cn(
                          'w-5 color-neutral-600 text-3 flex-none',
                          getLayerIcon(selectedLayerIcon),
                        )}
                      />
                    )}

                    <p className={cn('typo-text-s', 'color-neutral-600')}>
                      {truncateText(selectedLayerName, 45)}
                    </p>
                  </div>
                  <div className={cn('i-skand-dropdown', 'color-neutral-400', 'text-3')} />
                </div>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="center" className="z-5" side="bottom">
                  <LayerSelector
                    onClose={() => setEnabledLayerSelector(false)}
                    onSelect={handleSelectLayer}
                    selectedLayer={selectedLayer}
                  />
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {isLoading && <Loader />}
            {!isLoading && sections && shapeType === 'Polyline' && (
              <SectioningView sections={sections} />
            )}

            {!isLoading && volumetrics && shapeType === 'Polygon' && (
              <VolumetricsView volumetrics={volumetrics} />
            )}
            {!isLoading && !volumetrics && !sections && <ActionButton />}

            <div
              className="mt-3 flex flex-row cursor-pointer items-center gap-2 border-t-1 border-t-neutral-400 border-t-solid pt-3"
              onClick={() => setEnabledPoints(!enabledPoints)}
            >
              <div
                className={cn(
                  'i-skand-dropdown text-14px color-neutral-400 transition-transform',
                  enabledPoints ? 'rotate-180' : 'rotate-270',
                )}
              />
              <p className="text-neutral-600 typo-text-s-em">
                {enabledPoints ? 'Hide' : 'Show'} points
              </p>
            </div>
          </>
        )}
        <div className="skand-rnd-disable">
          {enabledPoints && shapeType !== 'Point' && (
            <AnnotationMetadata hideObjectDetails isClosed={isClosed} points={points} />
          )}
        </div>
      </div>

      <RemoveCalculationModal
        onCancel={handleCloseRemoveCalculationModal}
        onSubmit={handleConfirmRemoveCalculation}
        open={enabledRemoveCalculationModal}
        shapeType={shapeType}
      />
    </PopupMenu>
  );
};
